import { SinglePhotoSeasonTray, SinglePhotoSeasonTrayContentSort } from '../../../domain/single-photo-season-tray';
import { CreateSeasonPageItemRequestApiModel, SortOrderApiModel, UpdateSeasonPageItemRequestApiModel } from '@cxb/editor-stb-api';
import { mapperPipe, remainderMapper } from 'osn-shared';
import {
  platformTargetingMapper,
  targetingMapperDomainToApi,
  nameLocalizedMapperDomainToApi,
  scheduledDateMapperDomainToApi,
  displayServiceTypeIconMapperDomainToApi,
  descriptionLocalizedMapperDomainToApi,
} from '../../mapping/create-tray-domain-to-api';
import {
  commonMapper,
  descriptionLocalizedMapperApiToDomain,
  nameLocalizedMapperApiToDomain,
  scheduledDateMapperApiToDomain,
  targetingMapperApiToDomain,
} from '../../mapping/create-tray-api-to-domain';
import {
  SinglePhotoSeasonTrayFormViewModel,
  SeasonPageItemWithProgramApiModel,
  SinglePhotoSeasonTraySortViewModel,
} from './single-photo-season-tray-form.view-model';
import { TrayFormViewModelType } from '../view-model-common/view-model-type';
import {
  displayServiceTypeIconMapper,
  nameLocalizedMapperDomainToViewModel,
  scheduledDateMapperDomainToViewModel,
  targetingMapper,
} from '../create-tray-screen/mapping/create-tray-domain-to-vm';
import { scheduledDateMapperViewModelToDomain } from '../create-tray-screen/mapping/create-tray-vm-to-domain';

export const mapSinglePhotoSeasonTrayDomainModelToApiModel = (
  seasonTray: SinglePhotoSeasonTray,
  pageVersionId?: string,
  tenantId?: string
): CreateSeasonPageItemRequestApiModel & UpdateSeasonPageItemRequestApiModel => {
  const { layout, program, sort, image } = seasonTray;

  const commonProperties = mapperPipe(
    seasonTray,
    nameLocalizedMapperDomainToApi,
    targetingMapperDomainToApi,
    platformTargetingMapper,
    displayServiceTypeIconMapperDomainToApi,
    scheduledDateMapperDomainToApi,
    descriptionLocalizedMapperDomainToApi
  );

  return remainderMapper(commonProperties, () => ({
    descriptionVisible: true,
    layout,
    pageVersionId,
    seriesId: program?.id?.toString(),
    sortOrder: sort === SinglePhotoSeasonTrayContentSort.Ascending ? SortOrderApiModel.Ascending : SortOrderApiModel.Descending,
    imageId: image?.id,
    tenantId,
  }));
};

export const mapSinglePhotoSeasonTrayApiModelToDomainModel = (pageItem: SeasonPageItemWithProgramApiModel): SinglePhotoSeasonTray => {
  const { slug, image, sortOrder, layout, program } = pageItem;
  const splitSeriesId = pageItem.seriesId.split('/');
  const seriesId = splitSeriesId[splitSeriesId.length - 1];

  return remainderMapper(
    mapperPipe(
      pageItem,
      commonMapper,
      scheduledDateMapperApiToDomain,
      descriptionLocalizedMapperApiToDomain,
      nameLocalizedMapperApiToDomain,
      displayServiceTypeIconMapperDomainToApi,
      targetingMapperApiToDomain
    ),
    () => ({
      type: 'SinglePhotoSeasonTray',
      urlSlug: slug,
      seriesId,
      image: { id: image.id, name: image.originalName, publicUrl: image.publicUrl },
      sort:
        sortOrder === SortOrderApiModel.Ascending
          ? SinglePhotoSeasonTrayContentSort.Ascending
          : SinglePhotoSeasonTrayContentSort.Descending,
      program,
      layout,
    })
  );
};

export const mapSinglePhotoSeasonTrayDomainModelToViewModel = (tray: SinglePhotoSeasonTray): SinglePhotoSeasonTrayFormViewModel => {
  const { image, descriptionTextArabic, descriptionTextEnglish, sort, program, layout } = tray;
  const commonProperties = mapperPipe(
    tray,
    commonMapper,
    displayServiceTypeIconMapper,
    nameLocalizedMapperDomainToViewModel,
    targetingMapper,
    scheduledDateMapperDomainToViewModel
  );
  return remainderMapper(commonProperties, () => ({
    image,
    description: { en: descriptionTextEnglish, ar: descriptionTextArabic },
    type: TrayFormViewModelType.SinglePhotoSeasonTray,
    sort:
      sort === SinglePhotoSeasonTrayContentSort.Ascending
        ? SinglePhotoSeasonTraySortViewModel.Ascending
        : SinglePhotoSeasonTraySortViewModel.Descending,
    program,
    layout,
  }));
};

export const mapSinglePhotoSeasonTrayViewModelToDomainModel = (vm: SinglePhotoSeasonTrayFormViewModel): SinglePhotoSeasonTray => {
  const { image, title, description, sort, program, layout } = vm;
  const commonProperties = mapperPipe(
    vm,
    commonMapper,
    displayServiceTypeIconMapper,
    targetingMapper,
    scheduledDateMapperViewModelToDomain
  );

  return remainderMapper(commonProperties, () => ({
    image,
    titleEnglish: title.en,
    titleArabic: title.ar,
    descriptionTextEnglish: description.en,
    descriptionTextArabic: description.ar,
    draggable: false,
    order: undefined,
    type: 'SinglePhotoSeasonTray',
    visible: undefined,
    sort:
      sort === SinglePhotoSeasonTraySortViewModel.Ascending
        ? SinglePhotoSeasonTrayContentSort.Ascending
        : SinglePhotoSeasonTrayContentSort.Descending,
    program,
    layout,
  }));
};
